import { useState, useCallback } from 'react';
import api, { SocialMediaPost } from '@/lib/api';

export type ComposerStep = 'compose' | 'adapt' | 'schedule' | 'publish';

export function usePostComposer(existingPost?: SocialMediaPost) {
  const [post, setPost] = useState<SocialMediaPost | null>(existingPost ?? null);
  const [content, setContent] = useState(existingPost?.originalContent ?? '');
  const [targetAccountIds, setTargetAccountIds] = useState<string[]>(
    existingPost?.targetAccountIds ?? [],
  );
  const [step, setStep] = useState<ComposerStep>('compose');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveDraft = useCallback(
    async (overrideTargetAccountIds?: string[]) => {
      if (!content.trim()) return;
      setIsSaving(true);
      setError(null);
      const accountIds = overrideTargetAccountIds ?? targetAccountIds;
      try {
        if (post) {
          const updated = await api.updateSocialMediaPost(post.postId, {
            originalContent: content,
            targetAccountIds: accountIds,
          });
          setPost(updated);
          return updated;
        } else {
          const created = await api.createSocialMediaPost({
            originalContent: content,
            targetAccountIds: accountIds,
          });
          setPost(created);
          return created;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save draft');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [post, content, targetAccountIds],
  );

  const adaptContent = useCallback(
    async (platforms?: string[]) => {
      if (!post) return null;
      setIsSaving(true);
      setError(null);
      try {
        const result = await api.adaptSocialMediaPost(post.postId, platforms);
        setPost((prev) => (prev ? { ...prev, platformContent: result.platformContent } : prev));
        return result.platformContent;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to adapt content');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [post],
  );

  const schedulePost = useCallback(
    async (publishAt: string) => {
      if (!post) return null;
      setIsSaving(true);
      setError(null);
      try {
        const updated = await api.scheduleSocialMediaPost(post.postId, publishAt);
        setPost(updated);
        return updated;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to schedule post');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [post],
  );

  const publishNow = useCallback(async (postId: string) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.publishSocialMediaPostNow(postId);
      const updated = await api.getSocialMediaPost(postId);
      setPost(updated);
      return result.results;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish post');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    post,
    content,
    setContent,
    targetAccountIds,
    setTargetAccountIds,
    step,
    setStep,
    isSaving,
    error,
    saveDraft,
    adaptContent,
    schedulePost,
    publishNow,
  };
}
